import { Dispatch } from 'redux';
import { Transaction } from 'types/rest/assetCatalogTypes';
import { BlueprintDraft } from './blueprintsTypes';
import {
  setBlueprints,
  setAvailableTransactions,
  setBlueprintsLoading,
  setDetailLoading,
  upsertBlueprint,
  removeBlueprint,
  closeEditor,
  closeApply,
} from './blueprintsReducer';
import { nextBlueprintId } from './blueprintsMockData';
import { showToastNotification } from '../ui/toasts/toastsActions';
import { ToastType } from '../ui/toasts/toastsTypes';
import { ApplicationState } from '../rootReducer';
import { getErrorMessage } from '../../utils/miscUtil';
import { getActiveWorkspaceId } from '../../utils/bzmUserConfigUtil';
import acApi from '../../axios/ac/acApi';
import { BlueprintDto, toBlueprint, draftToBaseDto } from './blueprintsConverter';
import i18n from '../../i18n';
import MixpanelService from '../../utils/mixpanelService';
import { BLUEPRINT_CREATED, BLUEPRINT_UPDATED, BLUEPRINT_DELETED, BLUEPRINT_APPLIED } from '../../utils/mixpanelEvents';

// A freshly-picked thumbnail is a base64 data URL; an unchanged one is the image-endpoint URL
// (or empty). Returns null for anything that isn't a data URL, so callers can skip the upload.
const dataUrlToBlob = (value: string): Blob | null => {
  const match = /^data:(.+?);base64,(.*)$/.exec(value);
  if (!match) return null;
  const contentType = match[1];
  const binary = atob(match[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
};

// The blueprint image is stored via a separate multipart endpoint, not the JSON body.
// No-ops when the thumbnail isn't a newly-picked data URL.
const uploadThumbnail = async (blueprintId: number | string, thumbnail: string): Promise<void> => {
  const blob = dataUrlToBlob(thumbnail);
  if (!blob) return;
  const formData = new FormData();
  formData.append('file', blob, 'thumbnail');
  await acApi.blueprints.uploadImage(blueprintId, formData);
};

export const fetchBlueprintsThunk = () => async (dispatch: Dispatch) => {
  dispatch(setBlueprintsLoading(true));
  try {
    const response = await acApi.blueprints.list();
    const blueprints = (response.data.result as BlueprintDto[]).map(toBlueprint);
    dispatch(setBlueprints(blueprints));
    dispatch(setAvailableTransactions([]));
  } catch (e) {
    dispatch(showToastNotification(ToastType.Danger, 'Cannot load blueprints', getErrorMessage(e)));
  } finally {
    dispatch(setBlueprintsLoading(false));
  }
};

export const fetchBlueprintByIdThunk = (id: string) => async (dispatch: Dispatch) => {
  dispatch(setDetailLoading(true));
  try {
    const response = await acApi.blueprints.getById(id, { includeTransactions: true });
    dispatch(upsertBlueprint(toBlueprint(response.data.result as BlueprintDto)));
  } catch (e) {
    dispatch(showToastNotification(ToastType.Danger, 'Cannot load blueprint', getErrorMessage(e)));
  } finally {
    dispatch(setDetailLoading(false));
  }
};

// CREATE — snapshots transactions from a single service (serviceId derived in the modal).
export const createBlueprintThunk = (draft: BlueprintDraft, serviceId: number) => async (dispatch: Dispatch) => {
  MixpanelService.track(BLUEPRINT_CREATED(draft.transactionIds.length, draft.visibility), true);
  try {
    const txnFilter = draft.transactionIds.length > 0 ? draft.transactionIds : undefined;
    const response = await acApi.blueprints.create(serviceId, txnFilter, draftToBaseDto(draft));
    const created = response.data.result as BlueprintDto;
    if (draft.thumbnail && created && created.id != null) {
      try {
        await uploadThumbnail(created.id, draft.thumbnail);
      } catch (imgError) {
        dispatch(
          showToastNotification(
            ToastType.Warning,
            'Blueprint saved, but the thumbnail could not be uploaded',
            getErrorMessage(imgError),
          ),
        );
      }
    }
    dispatch(closeEditor());
    dispatch(showToastNotification(ToastType.Success, i18n.t('blueprints:toast.saved')));
    dispatch(fetchBlueprintsThunk() as never);
  } catch (e) {
    dispatch(showToastNotification(ToastType.Danger, 'Cannot save blueprint', getErrorMessage(e)));
  }
};

// UPDATE — metadata only (name/description/tags/scope). Returns true on success for navigation.
export const updateBlueprintThunk = (id: string, draft: BlueprintDraft) => async (
  dispatch: Dispatch,
): Promise<boolean> => {
  MixpanelService.track(BLUEPRINT_UPDATED(), true);
  try {
    await acApi.blueprints.update(id, draftToBaseDto(draft));
    if (draft.thumbnail) {
      try {
        await uploadThumbnail(id, draft.thumbnail);
      } catch (imgError) {
        dispatch(
          showToastNotification(
            ToastType.Warning,
            'Blueprint saved, but the thumbnail could not be uploaded',
            getErrorMessage(imgError),
          ),
        );
      }
    }
    dispatch(showToastNotification(ToastType.Success, i18n.t('blueprints:toast.saved')));
    return true;
  } catch (e) {
    dispatch(showToastNotification(ToastType.Danger, 'Cannot save blueprint', getErrorMessage(e)));
    return false;
  }
};

export const duplicateBlueprintThunk = (id: string) => (
  dispatch: Dispatch,
  getState: () => ApplicationState,
): string | null => {
  const source = getState().blueprints.blueprints.find(b => b.id === id);
  if (!source) return null;
  const newId = nextBlueprintId();
  dispatch(
    upsertBlueprint({
      ...source,
      id: newId,
      name: `${source.name} (copy)`,
      source: 'CUSTOM',
      visibility: 'PRIVATE',
      author: 'You',
      updatedAt: Date.now(),
    }),
  );
  dispatch(showToastNotification(ToastType.Success, i18n.t('blueprints:toast.duplicated')));
  return newId;
};

export const deleteBlueprintThunk = (id: string) => async (dispatch: Dispatch) => {
  MixpanelService.track(BLUEPRINT_DELETED(), true);
  try {
    await acApi.blueprints.delete(id);
    dispatch(removeBlueprint(id));
    dispatch(showToastNotification(ToastType.Success, i18n.t('blueprints:toast.deleted')));
  } catch (e) {
    dispatch(showToastNotification(ToastType.Danger, 'Cannot delete blueprint', getErrorMessage(e)));
  }
};

// APPLY — adds a blueprint's transactions to a service (new or existing).
export const applyBlueprintThunk = (
  id: string,
  target:
    | { newServiceName: string; serviceId?: never; serviceName?: never }
    | { serviceId: number; serviceName: string; newServiceName?: never },
  skipBlazeData: boolean,
) => async (dispatch: Dispatch): Promise<void> => {
  const targetType = 'serviceId' in target && target.serviceId != null ? 'existing' : 'new';
  MixpanelService.track(BLUEPRINT_APPLIED(targetType, skipBlazeData), true);
  try {
    const workspaceId = getActiveWorkspaceId();
    const params =
      'serviceId' in target && target.serviceId != null
        ? { workspaceId, serviceId: target.serviceId, skipBlazeData }
        : { workspaceId, newServiceName: (target.newServiceName as string).trim(), skipBlazeData };
    const response = await acApi.blueprints.apply(id, params);
    const count = Array.isArray(response.data.result) ? response.data.result.length : 0;
    const name =
      'serviceId' in target && target.serviceId != null ? target.serviceName : (target.newServiceName as string).trim();
    dispatch(closeApply());
    dispatch(showToastNotification(ToastType.Success, i18n.t('blueprints:toast.applied', { count, name })));
  } catch (e) {
    dispatch(showToastNotification(ToastType.Danger, 'Cannot add blueprint to catalog', getErrorMessage(e)));
  }
};
